// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { PropsWithChildren, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Button, ButtonProps, CircularProgress, SxProps } from '@mui/material';

type AsyncButtonProps = {
  sx?: SxProps;
  color?: ButtonProps['color'];
  size?: 'small' | 'medium' | 'large';
  variant?: 'contained' | 'text' | 'outlined';
  disabled?: boolean;
  onClick: () => Promise<unknown>;
};

export const AsyncButton: React.FC<PropsWithChildren<AsyncButtonProps>> = ({
  sx,
  color = 'primary',
  size = 'small',
  variant = 'contained',
  children,
  disabled,
  onClick,
}) => {
  const { showBoundary } = useErrorBoundary<Error>();
  const [loading, setLoading] = useState(false);

  const clickHandler = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setLoading(true);
    onClick()
      .catch((error: Error) => showBoundary(error))
      .finally(() => setLoading(false));
  };

  const buttonSx = {
    position: 'relative',
    paddingTop: 0,
    paddingBottom: 0,
    '& .MuiCircularProgress-root': {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: '-8px',
      marginLeft: '-8px',
    },
    '& .button-text': {
      opacity: loading ? 0 : 1,
    },
    ...sx,
  } as SxProps;

  return (
    <Button
      sx={buttonSx}
      color={color}
      size={size}
      variant={variant}
      disabled={disabled || loading}
      onClick={clickHandler}
    >
      {loading && <CircularProgress size={16} />}
      <span className="button-text">{children}</span>
    </Button>
  );
};
